// Marks the JS-enabled path so the CSS :hover/:focus-within fallback in
// header.liquid only applies when this module never runs.
document.documentElement.classList.add('js-nav');

const header = document.querySelector('[data-site-header]');
const triggers = Array.from(document.querySelectorAll('[data-mega-trigger]'));
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const openedByHover = new WeakSet();

function getPanel(trigger) {
  const id = trigger.getAttribute('aria-controls');
  return id ? document.getElementById(id) : null;
}

function openPanel(trigger) {
  const panel = getPanel(trigger);
  if (!panel) return;
  trigger.setAttribute('aria-expanded', 'true');
  panel.setAttribute('data-open', '');
}

function closePanel(trigger) {
  const panel = getPanel(trigger);
  trigger.setAttribute('aria-expanded', 'false');
  openedByHover.delete(trigger);
  if (panel) panel.removeAttribute('data-open');
}

function closeAllPanels(except) {
  triggers.forEach((trigger) => {
    if (trigger !== except) closePanel(trigger);
  });
}

triggers.forEach((trigger) => {
  const item = trigger.closest('.site-nav__item');

  trigger.addEventListener('click', () => {
    const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    closeAllPanels(trigger);
    if (wasOpen && !openedByHover.has(trigger)) {
      closePanel(trigger);
    } else {
      // If the panel was opened by hover, the click confirms it: keep it
      // open and hand ownership to the click state. Also covers touch,
      // where a tap can fire mouseenter immediately before click.
      openedByHover.delete(trigger);
      openPanel(trigger);
    }
  });

  if (item) {
    // The full-width Shop panel starts at the header's bottom edge, so the
    // pointer may briefly cross header background between the trigger and
    // the panel. Delay the hover-close so that crossing cannot dismiss the
    // panel; re-entering the item subtree (which includes the panel)
    // cancels the pending close.
    let hoverCloseTimer = null;

    item.addEventListener('mouseenter', () => {
      if (hoverCloseTimer) {
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = null;
      }
      closeAllPanels(trigger);
      if (trigger.getAttribute('aria-expanded') !== 'true') {
        openPanel(trigger);
        openedByHover.add(trigger);
      }
    });

    item.addEventListener('mouseleave', () => {
      if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
      hoverCloseTimer = setTimeout(() => {
        hoverCloseTimer = null;
        closePanel(trigger);
      }, 220);
    });
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  triggers.forEach((trigger) => {
    if (trigger.getAttribute('aria-expanded') === 'true') {
      closePanel(trigger);
      trigger.focus();
    }
  });
});

document.addEventListener('click', (event) => {
  triggers.forEach((trigger) => {
    const item = trigger.closest('.site-nav__item');
    if (item && !item.contains(event.target)) {
      closePanel(trigger);
    }
  });
});

document.addEventListener('focusin', (event) => {
  triggers.forEach((trigger) => {
    const item = trigger.closest('.site-nav__item');
    if (item && !item.contains(event.target)) {
      closePanel(trigger);
    }
  });
});

const drawerTrigger = document.querySelector('[data-drawer-trigger]');
const drawer = document.querySelector('[data-mobile-drawer]');

// The drawer is a full-screen sheet (CSS inset:0) with its own close
// button, covering the header entirely.
function openDrawer() {
  if (!drawerTrigger || !drawer) return;
  drawer.hidden = false;
  drawerTrigger.setAttribute('aria-expanded', 'true');
  document.documentElement.classList.add('drawer-open');
}

function closeDrawer() {
  if (!drawerTrigger || !drawer) return;
  drawer.hidden = true;
  drawerTrigger.setAttribute('aria-expanded', 'false');
  document.documentElement.classList.remove('drawer-open');
}

if (drawerTrigger && drawer) {
  drawerTrigger.addEventListener('click', () => {
    const isOpen = drawerTrigger.getAttribute('aria-expanded') === 'true';
    if (isOpen) {
      closeDrawer();
    } else {
      closeAllPanels();
      openDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (drawerTrigger.getAttribute('aria-expanded') === 'true') {
      closeDrawer();
      drawerTrigger.focus();
    }
  });

  document.addEventListener('click', (event) => {
    if (drawerTrigger.getAttribute('aria-expanded') !== 'true') return;
    if (drawer.contains(event.target) || drawerTrigger.contains(event.target)) return;
    closeDrawer();
  });

  const drawerClose = drawer.querySelector('[data-drawer-close]');
  if (drawerClose) {
    drawerClose.addEventListener('click', () => {
      closeDrawer();
      drawerTrigger.focus();
    });
  }

  const desktopQuery = window.matchMedia('(min-width: 52.0625rem)');
  const handleDesktopChange = (event) => {
    if (event.matches) closeDrawer();
  };
  if (desktopQuery.addEventListener) {
    desktopQuery.addEventListener('change', handleDesktopChange);
  } else if (desktopQuery.addListener) {
    desktopQuery.addListener(handleDesktopChange);
  }
}

// Search modal: the header search icon opens an in-place prompt instead
// of navigating to the search page (the link's href is the no-JS path).
const searchLink = document.querySelector('.site-header__search-link');
const searchModal = document.querySelector('[data-search-modal]');

if (searchLink && searchModal) {
  const searchInput = searchModal.querySelector('input[type="search"]');

  const openSearch = () => {
    searchModal.hidden = false;
    document.documentElement.classList.add('search-open');
    if (searchInput) searchInput.focus();
  };
  const closeSearch = () => {
    searchModal.hidden = true;
    document.documentElement.classList.remove('search-open');
  };

  searchLink.addEventListener('click', (event) => {
    event.preventDefault();
    openSearch();
  });
  searchModal.querySelectorAll('[data-search-close]').forEach((el) => {
    el.addEventListener('click', closeSearch);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !searchModal.hidden) {
      closeSearch();
      searchLink.focus();
    }
  });
}

if (header) {
  let lastScrollY = window.scrollY;
  let ticking = false;

  const updateHeaderVisibility = () => {
    const currentScrollY = window.scrollY;

    // Drives the background sheet: past the top, the header keeps its
    // solid backdrop (see .site-header::before in header.liquid).
    if (currentScrollY > 16) {
      header.setAttribute('data-scrolled', '');
    } else {
      header.removeAttribute('data-scrolled');
    }

    if (!prefersReducedMotion) {
      if (currentScrollY > lastScrollY && currentScrollY > header.offsetHeight) {
        header.setAttribute('data-hidden', '');
      } else {
        header.removeAttribute('data-hidden');
      }
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  updateHeaderVisibility();

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeaderVisibility);
        ticking = true;
      }
    },
    { passive: true }
  );
}
